import { workspaceChoiceMarkup } from './workspace-choice-control.mjs?v=20260806.100';

const MAX_POD_BYTES = 10 * 1024 * 1024;

const DISPATCHER_POD_STATUSES = Object.freeze([
  ['', 'No status update'],
  ['not_arrived', 'Not Arrived'],
  ['arrived_at_port', 'Arrived At Port'],
  ['available_for_pickup', 'Available For Pickup'],
  ['pending', 'Pending'],
  ['delivered_to_warehouse', 'Delivered To Warehouse'],
  ['empty_returned', 'Empty Returned'],
  ['cancelled', 'Cancelled'],
]);

function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

function roleToken(role) {
  return String(role || '').trim().toLowerCase();
}

// The clerk adds POD too (Alan, 2026-09-25: "POD也给文员加上"). Photos and notes
// only: the operational status beside them stays the dispatcher's.
export function canEditDashboardPod(role) {
  return ['admin', 'dispatcher', 'clerk'].includes(roleToken(role));
}

function validImage(file) {
  return file && typeof file === 'object'
    && typeof file.name === 'string'
    && String(file.type || '').startsWith('image/')
    && Number(file.size) > 0
    && Number(file.size) <= MAX_POD_BYTES;
}

function assetId() {
  return globalThis.crypto?.randomUUID?.()
    || `pod-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createPodUploadDraft({ containerId, role } = {}) {
  if (!String(containerId || '').trim() || !canEditDashboardPod(role)) {
    throw new TypeError('POD upload draft is not authorized.');
  }
  const dispatcher = roleToken(role) === 'dispatcher';
  let assets = [];
  let completeSetConfirmed = false;
  let error = '';

  const snapshot = () => ({
    containerId: String(containerId), role: roleToken(role), dispatcher, error,
    completeSetConfirmed,
    assets: assets.map((asset) => ({ ...asset })),
  });

  return Object.freeze({
    addFiles(files) {
      const next = [...(files || [])].filter(validImage).map((file) => ({
        id: assetId(), file, fileName: file.name, note: '', status: '',
      }));
      assets = [...assets, ...next];
      if (next.length) completeSetConfirmed = false;
      error = next.length ? '' : 'Choose an image up to 10 MB.';
      return snapshot();
    },
    remove(index) {
      assets = assets.filter((_, assetIndex) => assetIndex !== Number(index));
      completeSetConfirmed = false;
      error = '';
      return snapshot();
    },
    setNote(index, note) {
      const asset = assets[Number(index)];
      if (asset) asset.note = String(note || '').slice(0, 500);
      error = '';
      return snapshot();
    },
    setStatus(index, status) {
      const asset = assets[Number(index)];
      const value = String(status || '');
      if (asset && dispatcher && DISPATCHER_POD_STATUSES.some(([token]) => token === value)) {
        asset.status = value;
      }
      return snapshot();
    },
    setCompleteSetConfirmed(value) {
      completeSetConfirmed = value === true;
      error = '';
      return snapshot();
    },
    setError(message) {
      error = String(message || '');
      return snapshot();
    },
    snapshot,
    buildSubmission() {
      if (!assets.length) throw new Error('POD_FILE_REQUIRED');
      if (assets.some((asset) => !asset.note.trim())) throw new Error('NOTE_REQUIRED');
      if (!completeSetConfirmed) throw new Error('POD_COMPLETE_SET_CONFIRMATION_REQUIRED');
      return {
        containerId: String(containerId),
        completeSetConfirmed: true,
        assets: assets.map(({ file, note, status }) => ({
          file, note: note.trim(), status: dispatcher ? status : '',
        })),
      };
    },
  });
}

function statusOptions(selected = '') {
  return DISPATCHER_POD_STATUSES.map(([value, label]) => (
    `<option value="${escapeHtml(value)}"${value === selected ? ' selected' : ''}>${escapeHtml(label)}</option>`
  )).join('');
}

function workspaceStatusOptions() {
  const tones = {
    '': 'neutral',
    not_arrived: 'neutral',
    arrived_at_port: 'arrived',
    available_for_pickup: 'available',
    pending: 'pending',
    delivered_to_warehouse: 'delivered',
    empty_returned: 'empty-returned',
    cancelled: 'cancelled',
  };
  return DISPATCHER_POD_STATUSES.map(([value, label]) => ({ value, label, tone: tones[value] }));
}

export function podUploadEditorMarkup({
  containerNo = 'Container', role = '', assets = [], error = '', pending = false,
  workspace = false, embedded = false, completeSetConfirmed = false,
} = {}) {
  const dispatcher = roleToken(role) === 'dispatcher';
  const rows = assets.map((asset, index) => `
    <article class="pod-upload-editor__asset" data-pod-upload-asset="${index}">
      <div class="pod-upload-editor__file">
        <i class="ph ph-file-image" aria-hidden="true"></i>
        <strong>${escapeHtml(asset.fileName || `Photo ${index + 1}`)}</strong>
        <button type="button" data-pod-upload-remove="${index}" aria-label="Remove ${escapeHtml(asset.fileName || 'photo')}">
          <i class="ph ph-trash" aria-hidden="true"></i>
        </button>
      </div>
      <label class="pod-upload-editor__note">
        <span>Photo note <em>Required</em></span>
        <textarea rows="2" maxlength="500" data-pod-upload-note="${index}" placeholder="Describe what this photo confirms" required>${escapeHtml(asset.note || '')}</textarea>
      </label>
      ${dispatcher ? (workspace
    ? workspaceChoiceMarkup({
      name: `pod_status_${index}`,
      label: 'Container status',
      value: asset.status,
      options: workspaceStatusOptions(),
    })
    : `<label class="pod-upload-editor__status"><span>Container status</span><select data-pod-upload-status="${index}">${statusOptions(asset.status)}</select></label>`) : ''}
    </article>`).join('');

  return `
    ${workspace ? `<span data-workspace-container-number hidden>${escapeHtml(containerNo)}</span>` : ''}
    <form class="pod-upload-editor${workspace ? ' pod-upload-editor--workspace' : ''}${embedded ? ' pod-upload-editor--embedded' : ''}" data-pod-upload-form${workspace ? ' data-workspace-done-target' : ''} novalidate>
      ${workspace || embedded ? '' : `<header><p>POD PHOTOS</p><h2>${escapeHtml(containerNo)}</h2><span>Each photo needs a note before it can be saved.</span></header>`}
      <label class="pod-upload-editor__drop">
        <i class="ph ph-cloud-arrow-up" aria-hidden="true"></i>
        <strong>Browse photos</strong>
        <span>Images only, up to 10 MB each</span>
        <input type="file" accept="image/*" multiple data-pod-upload-files>
      </label>
      <div class="pod-upload-editor__assets">${rows}</div>
      <label class="pod-upload-editor__complete-set">
        <input type="checkbox" data-pod-upload-complete-set${completeSetConfirmed ? ' checked' : ''}${pending ? ' disabled' : ''}>
        <span><strong>Complete set confirmed</strong><small>I confirm this is the complete set of POD photos.</small></span>
      </label>
      ${error ? `<p class="pod-upload-editor__error" role="alert">${escapeHtml(error)}</p>` : ''}
      <button type="submit"${workspace ? ' hidden' : ' class="pod-upload-editor__submit"'}${pending ? ' disabled' : ''}>${pending ? 'Saving POD photos…' : 'Save POD photos'}</button>
    </form>`;
}
